import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import axios from 'axios';
import { 
  Sparkles, Plus, Sun, Moon, Star, Calendar, 
  ChevronRight, Loader2, Settings, LogOut, User,
  Clock, MapPin, Palette, Hash, Orbit, Heart, Menu, Shield, Download, Smartphone, Users, ArrowRight, Hand, Share2, HelpCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import AstrologySearchBar from '../components/AstrologyChat';
import LanguageToggle from '../components/LanguageToggle';
import ReferralSection from '../components/ReferralSection';
import DashboardLoveMatch from '../components/DashboardLoveMatch';
import NotificationSettings from '../components/NotificationSettings';
import { ShareButton } from '../components/ShareCard';
import VedicGuruWidget from '../components/VedicGuruWidget';

import { BACKEND_URL } from '../lib/backend';

const API = `${BACKEND_URL}/api`;

const COSMIC_BG = "https://static.prod-images.emergentagent.com/jobs/9785081a-8e48-4a15-82bd-7d61dc7d61ef/images/08e83ce154b7f78e874eff64dc321b78c6bed5db08d086886b94a30844b3e772.png";
const PLANETS_IMG = "https://static.prod-images.emergentagent.com/jobs/9785081a-8e48-4a15-82bd-7d61dc7d61ef/images/8cbf4d8737a8538b3f143ea705ab58cb988a27f38c4788dfd89e4c07071d9f66.png";

const COLOR_MAP = {
  'White': '#FFFFFF',
  'Red': '#EF4444',
  'Yellow': '#FBBF24',
  'Green': '#22C55E',
  'Blue': '#3B82F6',
  'Violet': '#8B5CF6',
  'Orange': '#F97316',
  'Pink': '#EC4899',
  'Gold': '#D4AF37',
  'Silver': '#94A3B8'
};

export const Dashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { t, language } = useLanguage();
  const { isDark } = useTheme();
  const [profiles, setProfiles] = useState([]);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [prediction, setPrediction] = useState(null);
  const [weeklyPrediction, setWeeklyPrediction] = useState(null);
  const [monthlyPrediction, setMonthlyPrediction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [predictionLoading, setPredictionLoading] = useState(false);
  const [weeklyLoading, setWeeklyLoading] = useState(false);
  const [monthlyLoading, setMonthlyLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('daily');
  const [chatProfileId, setChatProfileId] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [usageStats, setUsageStats] = useState(null);
  const [isPremium, setIsPremium] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isAppInstalled, setIsAppInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSInstall, setShowIOSInstall] = useState(false);

  // Check if app is already installed or if on iOS
  useEffect(() => {
    const standalone = window.matchMedia('(display-mode: standalone)').matches || 
                       window.navigator.standalone === true;
    setIsAppInstalled(standalone);
    
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);
    
    // Listen for install prompt
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    
    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    
    // Listen for app installed
    window.addEventListener('appinstalled', () => {
      setIsAppInstalled(true);
      setDeferredPrompt(null);
    });
    
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstallApp = async () => {
    if (isIOS) {
      setShowIOSInstall(true);
      return;
    }
    
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setIsAppInstalled(true);
      }
      setDeferredPrompt(null);
    }
  };

  // Admin emails list
  const ADMIN_EMAILS = ['demo@nakshatra.com', 'admin@nakshatra.com', 'shankhyan1995@gmail.com', 'kumar.edu12vk@gmail.com'];

  useEffect(() => {
    // Check if current user is admin
    if (user?.email && ADMIN_EMAILS.includes(user.email)) {
      setIsAdmin(true);
    }
    // Check subscription status from user object
    if (user?.subscription_tier && ['stellar', 'cosmic'].includes(user.subscription_tier)) {
      setIsPremium(true);
    }
  }, [user]);

  // Fetch usage stats on mount and when user changes
  useEffect(() => {
    if (user) {
      fetchUsageStats();
    }
  }, [user]);

  const fetchUsageStats = async () => {
    try {
      const response = await axios.get(`${API}/user/usage-stats`, { withCredentials: true });
      setUsageStats(response.data);
      // Always update premium status from API response (most reliable)
      setIsPremium(response.data.is_premium === true);
      console.log('Usage stats loaded:', response.data);
    } catch (error) {
      console.error('Error fetching usage stats:', error);
    }
  };

  useEffect(() => {
    fetchProfiles();
  }, []);

  useEffect(() => {
    if (selectedProfile) {
      fetchPrediction(selectedProfile.profile_id);
      // Reset weekly/monthly when profile changes
      setWeeklyPrediction(null);
      setMonthlyPrediction(null);
    }
  }, [selectedProfile]);

  // Fetch weekly/monthly when tab changes
  useEffect(() => {
    if (selectedProfile && activeTab === 'weekly' && !weeklyPrediction) {
      fetchWeeklyPrediction(selectedProfile.profile_id);
    }
    if (selectedProfile && activeTab === 'monthly' && !monthlyPrediction) {
      fetchMonthlyPrediction(selectedProfile.profile_id);
    }
  }, [activeTab, selectedProfile]);

  const fetchProfiles = async () => {
    try {
      const response = await axios.get(`${API}/profiles`, { withCredentials: true });
      setProfiles(response.data);
      if (response.data.length > 0) {
        setSelectedProfile(response.data[0]);
      } else {
        // No profiles - redirect to create first profile
        navigate('/profiles', { state: { firstTimeUser: true } });
      }
    } catch (error) {
      console.error('Error fetching profiles:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPrediction = async (profileId) => {
    setPredictionLoading(true);
    try {
      const response = await axios.get(`${API}/predictions/${profileId}/daily`, { 
        withCredentials: true 
      });
      setPrediction(response.data);
    } catch (error) {
      console.error('Error fetching prediction:', error);
    } finally {
      setPredictionLoading(false);
    }
  };

  const fetchWeeklyPrediction = async (profileId) => {
    setWeeklyLoading(true);
    try {
      const response = await axios.get(`${API}/predictions/${profileId}/weekly`, { 
        withCredentials: true 
      });
      setWeeklyPrediction(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        setWeeklyPrediction({ error: 'subscription_required', tier: 'stellar' });
      } else {
        console.error('Error fetching weekly prediction:', error);
      }
    } finally {
      setWeeklyLoading(false);
    }
  };

  const fetchMonthlyPrediction = async (profileId) => {
    setMonthlyLoading(true);
    try {
      const response = await axios.get(`${API}/predictions/${profileId}/monthly`, { 
        withCredentials: true 
      });
      setMonthlyPrediction(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        setMonthlyPrediction({ error: 'subscription_required', tier: 'cosmic' });
      } else {
        console.error('Error fetching monthly prediction:', error);
      }
    } finally {
      setMonthlyLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '?';
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-[#FFF9EF]' : 'bg-[#FFF9EF]'}`}>
        <div className="text-center">
          <Orbit className="w-12 h-12 text-[#C84B32] animate-spin mx-auto mb-4" />
          <p className={isDark ? 'text-[#6B6560]' : 'text-[#6B6560]'}>{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${isDark ? 'cosmic-bg' : 'bg-[#FFF9EF]'}`}>
      {/* iOS Install Instructions Modal */}
      <AnimatePresence>
        {showIOSInstall && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
            onClick={() => setShowIOSInstall(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-gradient-to-br from-white to-[#FCE9D9] rounded-2xl p-6 max-w-sm w-full border border-purple-500/30 shadow-2xl"
            >
              <div className="text-center mb-6">
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-[#C84B32] to-[#F5B942] flex items-center justify-center">
                  <Smartphone className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">Install Nakshatra</h3>
                <p className="text-[#6B6560] text-sm">Add to your home screen for quick access</p>
              </div>
              
              <div className="space-y-4 mb-6">
                <div className="flex items-start gap-3 p-3 bg-[#FCE9D9]/40 rounded-xl">
                  <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center text-purple-600 font-bold text-sm">1</div>
                  <div>
                    <p className="text-white text-sm">Tap the <strong>Share</strong> button</p>
                    <p className="text-[#6B6560] text-xs mt-1">At the bottom of your browser</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 bg-[#FCE9D9]/40 rounded-xl">
                  <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center text-purple-600 font-bold text-sm">2</div>
                  <div>
                    <p className="text-white text-sm">Select <strong>"Add to Home Screen"</strong></p>
                    <p className="text-[#6B6560] text-xs mt-1">Scroll down in the share menu</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 bg-[#FCE9D9]/40 rounded-xl">
                  <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center text-purple-600 font-bold text-sm">3</div>
                  <div>
                    <p className="text-white text-sm">Tap <strong>"Add"</strong></p>
                    <p className="text-[#6B6560] text-xs mt-1">Nakshatra will appear on your home screen</p>
                  </div>
                </div>
              </div>
              
              <Button
                onClick={() => setShowIOSInstall(false)}
                className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500"
              >
                Got it!
              </Button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Background effects */}
      {isDark && (
        <>
          <div className="fixed inset-0 stars-bg opacity-30 pointer-events-none" />
          <div className="fixed inset-0 nebula-bg pointer-events-none" />
        </>
      )}
      
      {/* Navigation */}
      <nav className={`sticky top-0 z-50 backdrop-blur-xl border-b ${isDark ? 'bg-[#FFF9EF] border-[#282622]/10' : 'bg-white/90 border-gray-200'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14 sm:h-16">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-[#C84B32] to-[#F5B942] flex items-center justify-center">
                <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
              <span className="font-['Fraunces'] text-xl sm:text-2xl font-semibold gradient-text">Nakshatra</span>
            </div>
            
            <div className="flex items-center gap-2 sm:gap-4">
              {/* Download App Button - Left of profile */}
              {!isAppInstalled && (
                <Button
                  onClick={handleInstallApp}
                  data-testid="nav-install-btn"
                  size="sm"
                  className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white text-xs sm:text-sm"
                >
                  <Download className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">{language === 'hi' ? 'ऐप डाउनलोड' : 'Get App'}</span>
                  <span className="sm:hidden">{language === 'hi' ? 'ऐप' : 'App'}</span>
                </Button>
              )}
              
              {!isPremium && (
                <Button
                  onClick={() => navigate('/subscription')}
                  data-testid="nav-upgrade-btn"
                  variant="outline"
                  size="sm"
                  className="border-[#C84B32]/50 text-[#C84B32] hover:bg-[#C84B32]/10 hidden sm:flex text-xs sm:text-sm"
                >
                  <Star className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                  {language === 'hi' ? 'अपग्रेड' : 'Upgrade'}
                </Button>
              )}
              
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button 
                    data-testid="user-menu-btn"
                    className={`flex items-center gap-2 p-1.5 sm:p-2 rounded-xl transition-colors ${isDark ? 'hover:bg-[#FCE9D9]/60' : 'hover:bg-gray-100'}`}
                  >
                    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-[#C84B32] to-[#F5B942] flex items-center justify-center">
                      <span className="text-white text-xs sm:text-sm font-semibold">
                        {getInitials(user?.name)}
                      </span>
                    </div>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className={`w-56 ${isDark ? 'bg-white border-[#282622]/10' : 'bg-white border-gray-200'}`}>
                  <div className="px-3 py-3">
                    <p className={isDark ? 'text-[#282622] font-medium' : 'text-gray-900 font-medium'}>{user?.name}</p>
                    <p className={`text-sm truncate ${isDark ? 'text-[#6B6560]' : 'text-[#6B6560]'}`}>{user?.email}</p>
                  </div>
                  <DropdownMenuSeparator className={isDark ? 'bg-[#FCE9D9]/70' : 'bg-gray-200'} />
                  <DropdownMenuItem 
                    onClick={() => navigate('/profiles')}
                    data-testid="menu-profiles"
                    className={`cursor-pointer ${isDark ? 'text-[#6B6560] hover:text-[#282622] hover:bg-[#FCE9D9]/60' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'}`}
                  >
                    <User className="w-4 h-4 mr-2" />
                    {t('nav.profiles')}
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => navigate('/subscription')}
                    data-testid="menu-subscription"
                    className={`cursor-pointer sm:hidden ${isDark ? 'text-[#6B6560] hover:text-[#282622] hover:bg-[#FCE9D9]/60' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'}`}
                  >
                    <Star className="w-4 h-4 mr-2" />
                    {t('nav.subscription')}
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => navigate('/kundli-milan')}
                    data-testid="menu-kundli-milan"
                    className={`cursor-pointer ${isDark ? 'text-[#6B6560] hover:text-[#282622] hover:bg-[#FCE9D9]/60' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'}`}
                  >
                    <Heart className="w-4 h-4 mr-2" />
                    {t('dashboard.kundliMatching')}
                  </DropdownMenuItem>
                  {!isAppInstalled && (
                    <>
                      <DropdownMenuSeparator className={isDark ? 'bg-[#FCE9D9]/70' : 'bg-gray-200'} />
                      <DropdownMenuItem 
                        onClick={handleInstallApp}
                        data-testid="menu-install-app"
                        className={`cursor-pointer ${isDark ? 'text-[#22C55E] hover:text-[#4ADE80] hover:bg-[#FCE9D9]/60' : 'text-green-600 hover:text-green-700 hover:bg-gray-100'}`}
                      >
                        <Download className="w-4 h-4 mr-2" />
                        {isIOS ? t('common.addToHomeScreen') : t('common.installApp')}
                      </DropdownMenuItem>
                    </>
                  )}
                  {isAdmin && (
                    <>
                      <DropdownMenuSeparator className={isDark ? 'bg-[#FCE9D9]/70' : 'bg-gray-200'} />
                      <DropdownMenuItem 
                        onClick={() => navigate('/admin')}
                        data-testid="menu-admin"
                        className={`cursor-pointer ${isDark ? 'text-[#C84B32] hover:text-[#C84B32] hover:bg-[#FCE9D9]/60' : 'text-[#C84B32] hover:text-[#C84B32] hover:bg-gray-100'}`}
                      >
                        <Shield className="w-4 h-4 mr-2" />
                        {t('nav.admin')}
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator className={isDark ? 'bg-[#FCE9D9]/70' : 'bg-gray-200'} />
                  <DropdownMenuItem 
                    onClick={() => navigate('/help')}
                    data-testid="menu-help"
                    className={`cursor-pointer ${isDark ? 'text-blue-600 hover:text-blue-700 hover:bg-blue-500/10' : 'text-blue-600 hover:text-blue-500 hover:bg-blue-50'}`}
                  >
                    <HelpCircle className="w-4 h-4 mr-2" />
                    {language === 'hi' ? 'सहायता' : 'Help & Feedback'}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className={isDark ? 'bg-[#FCE9D9]/70' : 'bg-gray-200'} />
                  <DropdownMenuItem 
                    onClick={handleLogout}
                    data-testid="menu-logout"
                    className="text-red-600 hover:text-red-700 hover:bg-red-500/10 cursor-pointer"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    {t('nav.logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              
              {/* Language Toggle */}
              <LanguageToggle />
            </div>
          </div>
        </div>
      </nav>

      <main className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Header - Emotional Copy */}
        <section className="mb-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className={`font-['Fraunces'] text-2xl sm:text-3xl lg:text-4xl mb-3 ${isDark ? 'text-[#282622]' : 'text-gray-900'}`}>
              {t('dashboard.welcome')}
            </h1>
            <p className={`cosmic-message text-base sm:text-lg mb-2 ${isDark ? '' : 'text-amber-700'}`}>
              {language === 'hi' ? 'ब्रह्मांड आपके लिए एक संदेश लेकर आया है' : 'The universe has a message for you'}
            </p>
            <p className={`text-sm ${isDark ? 'text-[#6B6560]' : 'text-gray-600'}`}>
              {t('dashboard.welcomeSubtitle')}
            </p>
          </motion.div>
        </section>

        {/* Big AI Search Bar - Primary Feature */}
        <section className="mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <AstrologySearchBar 
              profiles={profiles}
              selectedProfileId={chatProfileId || selectedProfile?.profile_id}
              onSelectProfile={setChatProfileId}
              isHomepage={true}
            />
          </motion.div>
        </section>

        {/* 🕉️ Vedic Guru Widget - Standalone */}
        <section className="mb-6" data-testid="vedic-guru-section">
          <VedicGuruWidget 
            language={language} 
            userName={user?.name || profiles[0]?.name || 'User'}
            userProfile={profiles.find(p => p.profile_id === selectedProfile) || profiles[0]}
          />
        </section>

        {/* Three Main Feature Cards */}
        <section className="mb-10">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {/* 🔮 Your Horoscope */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              onClick={() => selectedProfile && setActiveTab('daily')}
              className="card-premium relative p-6 cursor-pointer hover:border-[#C84B32]/30 transition-all group"
              data-testid="horoscope-card"
            >
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#C84B32]/20 to-[#F5B942]/20 flex items-center justify-center group-hover:from-[#C84B32]/30 group-hover:to-[#F5B942]/30 transition-all group-hover:scale-110">
                  <span className="text-2xl">🔮</span>
                </div>
                <div>
                  <h3 className="font-['Fraunces'] text-lg text-[#282622] group-hover:text-[#C84B32] transition-colors">{t('dashboard.horoscope')}</h3>
                  <p className="text-[#6B6560] text-xs">{t('dashboard.horoscopeDesc')}</p>
                </div>
              </div>
              <p className="text-[#6B6560] text-xs">
                {language === 'hi' ? 'आज के तारे क्या कहते हैं' : 'Your stars today'}
              </p>
            </motion.div>

            {/* 📜 Janam Kundli */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.25 }}
              onClick={() => navigate('/janam-kundli')}
              data-testid="janam-kundli-card"
              className="card-premium relative p-6 cursor-pointer hover:border-[#C84B32]/30 transition-all group"
            >
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#F59E0B]/20 to-[#F5B942]/20 flex items-center justify-center group-hover:from-[#F59E0B]/30 group-hover:to-[#F5B942]/30 transition-all group-hover:scale-110">
                  <span className="text-2xl">📜</span>
                </div>
                <div>
                  <h3 className="font-['Fraunces'] text-lg text-[#282622] group-hover:text-[#C84B32] transition-colors">{t('dashboard.janamKundli')}</h3>
                  <p className="text-[#6B6560] text-xs">{t('dashboard.janamKundliDesc')}</p>
                </div>
              </div>
              <p className="text-[#6B6560] text-xs">
                {language === 'hi' ? 'अपने ग्रहों की स्थिति जानें' : 'Know your planetary positions'}
              </p>
            </motion.div>

            {/* ❤️ Kundli Matching */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              onClick={() => navigate('/kundli-milan')}
              data-testid="kundli-matching-card"
              className="card-premium relative p-6 cursor-pointer hover:border-[#C84B32]/30 transition-all group"
            >
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#EC4899]/20 to-[#F5B942]/20 flex items-center justify-center group-hover:from-[#EC4899]/30 group-hover:to-[#F5B942]/30 transition-all group-hover:scale-110">
                  <span className="text-2xl">❤️</span>
                </div>
                <div>
                  <h3 className="font-['Fraunces'] text-lg text-[#282622] group-hover:text-[#C84B32] transition-colors">{t('dashboard.kundliMatching')}</h3>
                  <p className="text-[#6B6560] text-xs">{t('dashboard.kundliMatchingDesc')}</p>
                </div>
              </div>
              <p className="text-[#8A8279] text-xs">
                {t('dashboard.kundliMatchingInfo')}
              </p>
            </motion.div>

            {/* 💕 Quick Love Match - Gen-Z Feature */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
            >
              <DashboardLoveMatch />
            </motion.div>
          </div>
        </section>

        {/* Referral Section */}
        <section className="mb-10">
          <ReferralSection />
        </section>

        {/* Notification Settings */}
        <section className="mb-10">
          <NotificationSettings />
        </section>

        {/* Profile Selector */}
        <section className="mb-10">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-['Fraunces'] text-xl sm:text-2xl text-[#282622]">{t('common.selectProfile')}</h2>
            <Button
              onClick={() => navigate('/profiles')}
              variant="ghost"
              size="sm"
              data-testid="manage-profiles-btn"
              className="text-[#C84B32] hover:text-[#C84B32] hover:bg-[#C84B32]/10 text-xs sm:text-sm"
            >
              Manage <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4 ml-1" />
            </Button>
          </div>
          
          <div className="flex gap-3 sm:gap-6 overflow-x-auto pb-4 scrollbar-hide -mx-4 px-4 sm:mx-0 sm:px-0">
            {profiles.map((profile) => (
              <motion.button
                key={profile.profile_id}
                onClick={() => setSelectedProfile(profile)}
                data-testid={`profile-card-${profile.profile_id}`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className={`flex-shrink-0 flex flex-col items-center gap-2 sm:gap-3 p-3 sm:p-5 rounded-xl sm:rounded-2xl transition-all ${
                  selectedProfile?.profile_id === profile.profile_id
                    ? 'bg-gradient-to-br from-[#C84B32]/20 to-[#F5B942]/20 border-2 border-[#C84B32]'
                    : 'bg-[#FFF9EF] border-2 border-transparent hover:border-[#C84B32]/30'
                }`}
              >
                <div 
                  className="w-14 h-14 sm:w-20 sm:h-20 rounded-full flex items-center justify-center text-base sm:text-xl font-semibold relative"
                  style={{ 
                    background: `linear-gradient(135deg, ${profile.avatar_color}, #6A0DAD)` 
                  }}
                >
                  <span className="text-white">{getInitials(profile.name)}</span>
                  {selectedProfile?.profile_id === profile.profile_id && (
                    <div className="absolute -inset-1 rounded-full border-2 border-[#C84B32] animate-pulse" />
                  )}
                </div>
                <span className={`text-xs sm:text-sm font-medium max-w-[70px] sm:max-w-none truncate ${
                  selectedProfile?.profile_id === profile.profile_id ? 'text-[#C84B32]' : 'text-[#6B6560]'
                }`}>
                  {profile.name}
                </span>
              </motion.button>
            ))}
            
            <motion.button
              onClick={() => navigate('/profiles')}
              data-testid="add-profile-btn"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="flex-shrink-0 flex flex-col items-center justify-center gap-2 sm:gap-3 p-3 sm:p-5 rounded-xl sm:rounded-2xl bg-[#FFF9EF] border-2 border-dashed border-[#282622]/10 hover:border-[#C84B32]/50 transition-all w-20 sm:w-32"
            >
              <div className="w-14 h-14 sm:w-20 sm:h-20 rounded-full bg-white flex items-center justify-center border border-[#282622]/10">
                <Plus className="w-5 h-5 sm:w-8 sm:h-8 text-[#6B6560]" />
              </div>
              <span className="text-xs sm:text-sm text-[#6B6560]">Add</span>
            </motion.button>
          </div>
        </section>

        {/* Main Content */}
        {selectedProfile ? (
          <AnimatePresence mode="wait">
            <motion.div
              key={selectedProfile.profile_id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4 }}
            >
              {/* Profile Info Card */}
              <div className="cosmic-card p-4 sm:p-6 mb-6 sm:mb-8">
                <div className="flex items-start gap-5">
                  <div 
                    className="w-16 h-16 rounded-2xl flex items-center justify-center text-lg font-semibold flex-shrink-0"
                    style={{ 
                      background: `linear-gradient(135deg, ${selectedProfile.avatar_color}, #6A0DAD)` 
                    }}
                  >
                    <span className="text-white">{getInitials(selectedProfile.name)}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-['Fraunces'] text-3xl text-[#282622] mb-3">
                      {selectedProfile.name}
                    </h3>
                    <div className="flex flex-wrap gap-5 text-sm text-[#6B6560]">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-[#C84B32]" />
                        <span>{formatDate(selectedProfile.date_of_birth)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-[#C84B32]" />
                        <span>{selectedProfile.time_of_birth}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-[#C84B32]" />
                        <span>{selectedProfile.place_of_birth}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Prediction Tabs */}
              <Tabs defaultValue="daily" className="space-y-8" onValueChange={setActiveTab}>
                <TabsList className="bg-white border border-[#282622]/10 rounded-xl p-1">
                  <TabsTrigger 
                    value="daily"
                    data-testid="tab-daily"
                    className="rounded-lg data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#C84B32] data-[state=active]:to-[#F5B942] data-[state=active]:text-white font-medium px-6"
                  >
                    Today
                  </TabsTrigger>
                  <TabsTrigger 
                    value="weekly"
                    data-testid="tab-weekly"
                    className="rounded-lg data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#C84B32] data-[state=active]:to-[#F5B942] data-[state=active]:text-white font-medium px-6"
                  >
                    Weekly
                  </TabsTrigger>
                  <TabsTrigger 
                    value="monthly"
                    data-testid="tab-monthly"
                    className="rounded-lg data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#C84B32] data-[state=active]:to-[#F5B942] data-[state=active]:text-white font-medium px-6"
                  >
                    Monthly
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="daily" className="space-y-8">
                  {predictionLoading ? (
                    <div className="flex items-center justify-center py-16">
                      <div className="text-center">
                        <Orbit className="w-10 h-10 text-[#C84B32] animate-spin mx-auto mb-4" />
                        <p className="text-[#6B6560]">Reading the stars...</p>
                      </div>
                    </div>
                  ) : prediction ? (
                    <>
                      {/* Vedic Chart Summary */}
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                        {[
                          { label: t('common.lagna'), value: prediction.lagna, icon: Sun },
                          { label: t('common.moonSign'), value: prediction.moon_sign, icon: Moon },
                          { label: t('common.nakshatra'), value: prediction.nakshatra, icon: Star },
                          { label: t('common.mahadasha'), value: prediction.mahadasha, icon: Orbit },
                          { label: t('common.antardasha'), value: prediction.antardasha, icon: Sparkles }
                        ].map((item, index) => (
                          <motion.div 
                            key={index} 
                            className="vedic-grid-item"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.1 }}
                          >
                            <div className="flex items-center gap-2 mb-2">
                              <item.icon className="w-3 h-3 text-[#C84B32]" />
                              <p className="text-[#C84B32] text-xs uppercase tracking-wider">{item.label}</p>
                            </div>
                            <p className="text-[#282622] text-sm font-medium">{item.value}</p>
                          </motion.div>
                        ))}
                      </div>

                      {/* Main Prediction Card */}
                      <div className="prediction-card p-8">
                        <div className="flex items-center gap-5 mb-8">
                          <div className="relative">
                            <img 
                              src={PLANETS_IMG}
                              alt="9 Grahas"
                              className="w-20 h-20 object-contain"
                            />
                          </div>
                          <div>
                            <p className="text-[#C84B32] text-xs uppercase tracking-[0.2em]">Today's Cosmic Reading</p>
                            <p className="font-['Fraunces'] text-3xl text-[#282622]">
                              {new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' })}
                            </p>
                          </div>
                        </div>

                        {/* Transit Summary */}
                        <div className="mb-8 p-5 bg-[#FFF9EF] rounded-2xl border border-[#282622]/10">
                          <div className="flex items-center gap-2 mb-3">
                            <Orbit className="w-4 h-4 text-[#C84B32]" />
                            <p className="text-[#C84B32] text-xs uppercase tracking-[0.2em]">Planetary Transit (Gochar)</p>
                          </div>
                          <p className="text-[#6B6560] text-sm leading-relaxed">{prediction.gochar_summary}</p>
                        </div>

                        {/* What May Happen */}
                        <div className="mb-8">
                          <div className="flex items-center gap-3 mb-4">
                            <Star className="w-5 h-5 text-[#C84B32]" />
                            <h4 className="font-['Fraunces'] text-2xl text-[#282622]">What the Stars Reveal</h4>
                          </div>
                          <p className="text-[#6B6560] leading-relaxed">{prediction.prediction_summary}</p>
                        </div>

                        {/* What To Do */}
                        <div className="mb-8">
                          <div className="flex items-center gap-3 mb-4">
                            <Sun className="w-5 h-5 text-[#C84B32]" />
                            <h4 className="font-['Fraunces'] text-2xl text-[#282622]">Cosmic Guidance</h4>
                          </div>
                          <p className="text-[#6B6560] leading-relaxed">{prediction.actionable_advice}</p>
                        </div>

                        {/* Lucky Elements */}
                        <div className="grid grid-cols-2 gap-5 mb-8">
                          <div className="bg-[#FFF9EF] rounded-2xl p-5 border border-[#282622]/10">
                            <div className="flex items-center gap-2 mb-3">
                              <Palette className="w-4 h-4 text-[#C84B32]" />
                              <p className="text-[#C84B32] text-xs uppercase tracking-wider">Lucky Color</p>
                            </div>
                            <div className="flex items-center gap-4">
                              <div 
                                className="lucky-color-box"
                                style={{ backgroundColor: COLOR_MAP[prediction.lucky_color] || prediction.lucky_color }}
                              />
                              <span className="text-[#282622] font-medium text-lg">{prediction.lucky_color}</span>
                            </div>
                          </div>
                          
                          <div className="bg-[#FFF9EF] rounded-2xl p-5 border border-[#282622]/10">
                            <div className="flex items-center gap-2 mb-3">
                              <Hash className="w-4 h-4 text-[#C84B32]" />
                              <p className="text-[#C84B32] text-xs uppercase tracking-wider">Lucky Number</p>
                            </div>
                            <span className="font-['Fraunces'] text-5xl text-[#282622]">
                              {prediction.lucky_number}
                            </span>
                          </div>
                        </div>

                        {/* Share Button */}
                        <div className="flex justify-center mt-4">
                          <ShareButton 
                            type="prediction"
                            data={{
                              summary: prediction.prediction_summary,
                              lucky_color: prediction.lucky_color,
                              lucky_number: prediction.lucky_number
                            }}
                            userName={selectedProfile?.name}
                          />
                        </div>

                        {/* Remedy */}
                        {prediction.remedy && (
                          <div className="bg-gradient-to-r from-[#C84B32]/10 via-[#F5B942]/10 to-transparent rounded-2xl p-5 border border-[#C84B32]/20">
                            <div className="flex items-center gap-2 mb-3">
                              <Moon className="w-4 h-4 text-[#C84B32]" />
                              <p className="text-[#C84B32] text-xs uppercase tracking-[0.2em]">Today's Vedic Remedy</p>
                            </div>
                            <p className="text-[#282622] text-sm leading-relaxed">{prediction.remedy}</p>
                          </div>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-16 text-[#6B6560]">
                      Unable to load cosmic predictions. Please try again.
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="weekly">
                  {weeklyLoading ? (
                    <div className="flex items-center justify-center py-16">
                      <div className="text-center">
                        <Orbit className="w-10 h-10 text-[#C84B32] animate-spin mx-auto mb-4" />
                        <p className="text-[#6B6560]">Calculating weekly transits...</p>
                      </div>
                    </div>
                  ) : weeklyPrediction?.error === 'subscription_required' ? (
                    <div className="cosmic-card p-10 text-center">
                      <Moon className="w-16 h-16 text-[#C84B32] mx-auto mb-6" />
                      <h3 className="font-['Fraunces'] text-2xl text-[#282622] mb-3">Weekly Cosmic Insights</h3>
                      <p className="text-[#6B6560] mb-6">{language === 'hi' ? 'साप्ताहिक ग्रह पूर्वानुमान अनलॉक करने के लिए Stellar प्लान में अपग्रेड करें' : 'Upgrade to Stellar plan to unlock weekly planetary forecasts'}</p>
                      <Button
                        onClick={() => navigate('/subscription')}
                        data-testid="upgrade-weekly-btn"
                        className="bg-gradient-to-r from-[#C84B32] to-[#F5B942] text-white hover:from-[#E85D43] hover:to-[#F5B942] font-medium"
                      >
                        Unlock Weekly Insights
                      </Button>
                    </div>
                  ) : weeklyPrediction ? (
                    <div className="space-y-6">
                      {/* Week Header */}
                      <div className="prediction-card p-6">
                        <div className="flex items-center gap-5 mb-6">
                          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#C84B32] to-[#F5B942] flex items-center justify-center">
                            <Calendar className="w-8 h-8 text-white" />
                          </div>
                          <div>
                            <p className="text-[#C84B32] text-xs uppercase tracking-[0.2em]">Weekly Forecast</p>
                            <p className="font-['Fraunces'] text-2xl text-[#282622]">
                              {new Date(weeklyPrediction.week_start).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} - {new Date(weeklyPrediction.week_end).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                            </p>
                          </div>
                        </div>
                        
                        <div className="p-5 bg-[#FFF9EF] rounded-2xl border border-[#282622]/10 mb-6">
                          <h4 className="text-[#C84B32] text-sm uppercase tracking-wider mb-3">Week Overview</h4>
                          <p className="text-[#282622] leading-relaxed">{weeklyPrediction.weekly_overview}</p>
                        </div>

                        <div className="grid md:grid-cols-2 gap-4">
                          <div className="bg-[#FFF9EF] rounded-xl p-4 border border-[#282622]/10">
                            <div className="flex items-center gap-2 mb-2">
                              <Sparkles className="w-4 h-4 text-[#C84B32]" />
                              <h5 className="text-[#C84B32] text-xs uppercase">Career & Finance</h5>
                            </div>
                            <p className="text-[#6B6560] text-sm">{weeklyPrediction.career_finance}</p>
                          </div>
                          
                          <div className="bg-[#FFF9EF] rounded-xl p-4 border border-[#282622]/10">
                            <div className="flex items-center gap-2 mb-2">
                              <Heart className="w-4 h-4 text-[#C84B32]" />
                              <h5 className="text-[#C84B32] text-xs uppercase">Relationships</h5>
                            </div>
                            <p className="text-[#6B6560] text-sm">{weeklyPrediction.relationships}</p>
                          </div>
                          
                          <div className="bg-[#FFF9EF] rounded-xl p-4 border border-[#282622]/10">
                            <div className="flex items-center gap-2 mb-2">
                              <Sun className="w-4 h-4 text-[#C84B32]" />
                              <h5 className="text-[#C84B32] text-xs uppercase">Health & Wellness</h5>
                            </div>
                            <p className="text-[#6B6560] text-sm">{weeklyPrediction.health_wellness}</p>
                          </div>
                          
                          <div className="bg-[#FFF9EF] rounded-xl p-4 border border-[#282622]/10">
                            <div className="flex items-center gap-2 mb-2">
                              <Star className="w-4 h-4 text-[#C84B32]" />
                              <h5 className="text-[#C84B32] text-xs uppercase">Spiritual Guidance</h5>
                            </div>
                            <p className="text-[#6B6560] text-sm">{weeklyPrediction.spiritual_guidance}</p>
                          </div>
                        </div>

                        {/* Lucky Days & Challenges */}
                        <div className="mt-6 grid md:grid-cols-2 gap-4">
                          <div className="bg-gradient-to-r from-[#C84B32]/10 to-transparent rounded-xl p-4 border border-[#C84B32]/20">
                            <h5 className="text-[#C84B32] text-xs uppercase mb-2">Lucky Days</h5>
                            <div className="flex gap-2 flex-wrap">
                              {weeklyPrediction.lucky_days?.map((day, i) => (
                                <span key={i} className="px-3 py-1 bg-[#C84B32]/20 rounded-full text-[#C84B32] text-sm">{day}</span>
                              ))}
                            </div>
                          </div>
                          
                          <div className="bg-[#C84B32]/10 rounded-xl p-4 border border-[#C84B32]/20">
                            <h5 className="text-[#6B6560] text-xs uppercase mb-2">Challenges</h5>
                            <p className="text-[#6B6560] text-sm">{weeklyPrediction.challenges}</p>
                          </div>
                        </div>

                        {/* Remedies */}
                        <div className="mt-6 p-4 bg-[#FFF9EF] rounded-xl border border-[#282622]/10">
                          <h5 className="text-[#C84B32] text-xs uppercase mb-3">Weekly Remedies</h5>
                          <ul className="space-y-2">
                            {weeklyPrediction.remedies?.map((remedy, i) => (
                              <li key={i} className="flex items-start gap-2 text-[#6B6560] text-sm">
                                <Moon className="w-4 h-4 text-[#C84B32] mt-0.5 flex-shrink-0" />
                                {remedy}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-16 text-[#6B6560]">
                      Unable to load weekly predictions. Please try again.
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="monthly">
                  {monthlyLoading ? (
                    <div className="flex items-center justify-center py-16">
                      <div className="text-center">
                        <Orbit className="w-10 h-10 text-[#C84B32] animate-spin mx-auto mb-4" />
                        <p className="text-[#6B6560]">Analyzing monthly celestial patterns...</p>
                      </div>
                    </div>
                  ) : monthlyPrediction?.error === 'subscription_required' ? (
                    <div className="cosmic-card p-10 text-center">
                      <Calendar className="w-16 h-16 text-[#C84B32] mx-auto mb-6" />
                      <h3 className="font-['Fraunces'] text-2xl text-[#282622] mb-3">Monthly Celestial Forecast</h3>
                      <p className="text-[#6B6560] mb-6">{language === 'hi' ? 'व्यापक मासिक पूर्वानुमान के लिए Cosmic प्लान में अपग्रेड करें' : 'Upgrade to Cosmic plan for comprehensive monthly predictions'}</p>
                      <Button
                        onClick={() => navigate('/subscription')}
                        data-testid="upgrade-monthly-btn"
                        className="bg-gradient-to-r from-[#C84B32] to-[#F5B942] text-white hover:from-[#E85D43] hover:to-[#F5B942] font-medium"
                      >
                        Unlock Monthly Forecast
                      </Button>
                    </div>
                  ) : monthlyPrediction ? (
                    <div className="space-y-6">
                      {/* Month Header */}
                      <div className="prediction-card p-6">
                        <div className="flex items-center gap-5 mb-6">
                          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#F5B942] to-[#F5B942] flex items-center justify-center">
                            <Sparkles className="w-8 h-8 text-white" />
                          </div>
                          <div>
                            <p className="text-[#C84B32] text-xs uppercase tracking-[0.2em]">Monthly Celestial Forecast</p>
                            <p className="font-['Fraunces'] text-3xl text-[#282622]">
                              {monthlyPrediction.month} {monthlyPrediction.year}
                            </p>
                          </div>
                        </div>
                        
                        {/* Major Transits */}
                        <div className="p-5 bg-gradient-to-r from-[#F5B942]/20 to-[#F5B942]/10 rounded-2xl border border-[#C84B32]/30 mb-6">
                          <div className="flex items-center gap-2 mb-3">
                            <Orbit className="w-4 h-4 text-[#C84B32]" />
                            <h4 className="text-[#C84B32] text-sm uppercase tracking-wider">Major Planetary Transits</h4>
                          </div>
                          <p className="text-[#282622] leading-relaxed">{monthlyPrediction.major_transits}</p>
                        </div>

                        <div className="p-5 bg-[#FFF9EF] rounded-2xl border border-[#282622]/10 mb-6">
                          <h4 className="text-[#C84B32] text-sm uppercase tracking-wider mb-3">Month Overview</h4>
                          <p className="text-[#282622] leading-relaxed">{monthlyPrediction.monthly_overview}</p>
                        </div>

                        <div className="grid md:grid-cols-2 gap-4">
                          <div className="bg-[#FFF9EF] rounded-xl p-4 border border-[#282622]/10">
                            <div className="flex items-center gap-2 mb-2">
                              <Sparkles className="w-4 h-4 text-[#C84B32]" />
                              <h5 className="text-[#C84B32] text-xs uppercase">Career & Finance</h5>
                            </div>
                            <p className="text-[#6B6560] text-sm">{monthlyPrediction.career_finance}</p>
                          </div>
                          
                          <div className="bg-[#FFF9EF] rounded-xl p-4 border border-[#282622]/10">
                            <div className="flex items-center gap-2 mb-2">
                              <Heart className="w-4 h-4 text-[#C84B32]" />
                              <h5 className="text-[#C84B32] text-xs uppercase">Relationships</h5>
                            </div>
                            <p className="text-[#6B6560] text-sm">{monthlyPrediction.relationships}</p>
                          </div>
                          
                          <div className="bg-[#FFF9EF] rounded-xl p-4 border border-[#282622]/10">
                            <div className="flex items-center gap-2 mb-2">
                              <Sun className="w-4 h-4 text-[#C84B32]" />
                              <h5 className="text-[#C84B32] text-xs uppercase">Health & Wellness</h5>
                            </div>
                            <p className="text-[#6B6560] text-sm">{monthlyPrediction.health_wellness}</p>
                          </div>
                          
                          <div className="bg-[#FFF9EF] rounded-xl p-4 border border-[#282622]/10">
                            <div className="flex items-center gap-2 mb-2">
                              <Star className="w-4 h-4 text-[#C84B32]" />
                              <h5 className="text-[#C84B32] text-xs uppercase">Spiritual Growth</h5>
                            </div>
                            <p className="text-[#6B6560] text-sm">{monthlyPrediction.spiritual_growth}</p>
                          </div>
                        </div>

                        {/* Auspicious Dates */}
                        <div className="mt-6 p-4 bg-gradient-to-r from-[#C84B32]/10 to-transparent rounded-xl border border-[#C84B32]/20">
                          <h5 className="text-[#C84B32] text-xs uppercase mb-3">Auspicious Dates</h5>
                          <div className="space-y-2">
                            {monthlyPrediction.auspicious_dates?.map((date, i) => (
                              <div key={i} className="flex items-center gap-2 text-[#282622] text-sm">
                                <Calendar className="w-4 h-4 text-[#C84B32]" />
                                {date}
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Gemstone Recommendation */}
                        <div className="mt-6 p-4 bg-[#FFF9EF] rounded-xl border border-[#C84B32]/30">
                          <div className="flex items-center gap-2 mb-2">
                            <Sparkles className="w-4 h-4 text-[#C84B32]" />
                            <h5 className="text-[#C84B32] text-xs uppercase">Recommended Gemstone</h5>
                          </div>
                          <p className="text-[#282622] font-medium">{monthlyPrediction.gemstone_recommendation}</p>
                        </div>

                        {/* Challenges */}
                        <div className="mt-6 p-4 bg-[#C84B32]/10 rounded-xl border border-[#C84B32]/20">
                          <h5 className="text-[#6B6560] text-xs uppercase mb-2">Challenges to Navigate</h5>
                          <p className="text-[#6B6560] text-sm">{monthlyPrediction.challenges}</p>
                        </div>

                        {/* Remedies */}
                        <div className="mt-6 p-4 bg-[#FFF9EF] rounded-xl border border-[#282622]/10">
                          <h5 className="text-[#C84B32] text-xs uppercase mb-3">Monthly Remedies</h5>
                          <ul className="grid md:grid-cols-2 gap-2">
                            {monthlyPrediction.remedies?.map((remedy, i) => (
                              <li key={i} className="flex items-start gap-2 text-[#6B6560] text-sm">
                                <Moon className="w-4 h-4 text-[#C84B32] mt-0.5 flex-shrink-0" />
                                {remedy}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-16 text-[#6B6560]">
                      Unable to load monthly predictions. Please try again.
                    </div>
                  )}
                </TabsContent>
              </Tabs>

              {/* Kundli Milan Card */}
              {profiles.length >= 2 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-8"
                >
                  <div className="cosmic-card p-6 bg-gradient-to-r from-[#EC4899]/10 to-[#F5B942]/10 border border-[#EC4899]/20">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#EC4899] to-[#F5B942] flex items-center justify-center">
                          <Heart className="w-7 h-7 text-white" />
                        </div>
                        <div>
                          <h3 className="font-['Fraunces'] text-xl text-[#282622] mb-1">Kundli Milan</h3>
                          <p className="text-[#6B6560] text-sm">Check marriage compatibility between profiles</p>
                        </div>
                      </div>
                      <Button
                        onClick={() => navigate('/kundli-milan')}
                        data-testid="kundli-milan-btn"
                        className="bg-gradient-to-r from-[#EC4899] to-[#F5B942] text-white hover:from-[#EC4899]/90 hover:to-[#F5B942]/90"
                      >
                        Check Compatibility
                      </Button>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* New Features Row */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-8 grid md:grid-cols-2 gap-4"
              >
                {/* Yearly Forecast Card */}
                <div className="cosmic-card p-5 bg-gradient-to-r from-[#7C3AED]/10 to-[#F5B942]/10 border border-[#7C3AED]/20 hover:border-[#7C3AED]/40 transition-colors cursor-pointer group"
                     onClick={() => navigate('/yearly-forecast')}
                     data-testid="yearly-forecast-card">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#7C3AED] to-[#F5B942] flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Calendar className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-['Fraunces'] text-lg text-[#282622] mb-1">Yearly Forecast</h3>
                      <p className="text-[#6B6560] text-xs">Your complete {new Date().getFullYear()} cosmic prediction</p>
                    </div>
                    <ArrowRight className="w-5 h-5 text-[#C84B32] opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </div>

                {/* Family Compatibility Card */}
                {profiles.length >= 2 && (
                  <div className="cosmic-card p-5 bg-gradient-to-r from-[#10B981]/10 to-[#F5B942]/10 border border-[#10B981]/20 hover:border-[#10B981]/40 transition-colors cursor-pointer group"
                       onClick={() => navigate('/family-compatibility')}
                       data-testid="family-compatibility-card">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#10B981] to-[#F5B942] flex items-center justify-center group-hover:scale-110 transition-transform">
                        <Users className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-['Fraunces'] text-lg text-[#282622] mb-1">Family Compatibility</h3>
                        <p className="text-[#6B6560] text-xs">Analyze cosmic bonds between family members</p>
                      </div>
                      <ArrowRight className="w-5 h-5 text-[#C84B32] opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </div>
                )}
              </motion.div>

              {/* Palm Reading Feature Card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-8"
              >
                <div className="cosmic-card p-6 bg-gradient-to-r from-[#C84B32]/10 to-[#F5B942]/10 border border-[#C84B32]/20 hover:border-[#C84B32]/40 transition-all cursor-pointer group relative overflow-hidden"
                     onClick={() => navigate('/palm-reading')}
                     data-testid="palm-reading-card">
                  {/* Decorative background */}
                  <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-[#C84B32]/20 to-transparent rounded-full blur-2xl group-hover:scale-150 transition-transform" />
                  
                  <div className="relative z-10 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#C84B32] to-[#F5B942] flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg shadow-[#C84B32]/30">
                        <Hand className="w-7 h-7 text-white" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-['Fraunces'] text-xl text-[#282622]">Palm Reading</h3>
                          <span className="px-2 py-0.5 text-[10px] font-semibold bg-gradient-to-r from-[#C84B32] to-[#F5B942] text-white rounded-full">NEW</span>
                        </div>
                        <p className="text-[#6B6560] text-sm">Scan your palm to reveal your destiny</p>
                      </div>
                    </div>
                    <ArrowRight className="w-6 h-6 text-[#C84B32] group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </AnimatePresence>
        ) : (
          <div className="cosmic-card p-6 sm:p-10 text-center">
            <Plus className="w-12 h-12 sm:w-16 sm:h-16 text-[#C84B32] mx-auto mb-4 sm:mb-6" />
            <h3 className="font-['Fraunces'] text-xl sm:text-2xl text-[#282622] mb-2 sm:mb-3">Map Your Stars</h3>
            <p className="text-[#6B6560] text-sm sm:text-base mb-4 sm:mb-6">Add a profile with your birth details to receive cosmic guidance</p>
            <Button
              onClick={() => navigate('/profiles')}
              data-testid="create-first-profile-btn"
              className="bg-gradient-to-r from-[#C84B32] to-[#F5B942] text-white hover:from-[#E85D43] hover:to-[#F5B942] font-medium"
            >
              Create Profile
            </Button>
          </div>
        )}
      </main>
    </div>
  );
};

export default Dashboard;
